import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { getVoiceSocketUrl, streamMessage } from './api.js';
import { useSigmundAuth } from './auth/useSigmundAuth.js';
import { checkFirstVisit } from './auth/firstVisitCheck.js';
import { LoginScreen } from './components/LoginScreen.js';
import { SigmundWelcome } from './components/SigmundWelcome.js';
import { ChatSidePanel } from './components/ChatSidePanel.js';
import { ModeSwitch } from './components/ModeSwitch.js';
import { VoiceConsole } from './components/VoiceConsole.js';
import { useVoiceAgent } from './hooks/useVoiceAgent.js';

export function App() {
  const [mode, setMode] = useState('');
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [agentActions, setAgentActions] = useState([]);
  const [isChatPanelOpen, setIsChatPanelOpen] = useState(false);
  const [sessionId] = useState(() => crypto.randomUUID());
  const [welcomePhase, setWelcomePhase] = useState('checking'); // checking | welcome | done
  const [mediaLoaded, setMediaLoaded] = useState(false);
  const welcomeChecked = useRef(false);

  // Preload the video immediately into browser cache
  useEffect(() => {
    fetch(process.env.PUBLIC_URL + '/hello-world.mov')
      .then((res) => res.blob())
      .then(() => setMediaLoaded(true))
      .catch((err) => {
        console.error('Failed to preload welcome video:', err);
        setMediaLoaded(true); // Don't block forever if download fails
      });
  }, []);

  const {
    user,
    companyUid,
    authToken,
    isAuthenticated,
    authReady,
    authLoading,
    authError,
    loginWithEmail,
    logout
  } = useSigmundAuth();

  const canUseVoice = useMemo(() => isAuthenticated && !loading, [isAuthenticated, loading]);
  const canSendText = useMemo(
    () => isAuthenticated && input.trim().length > 0 && !loading,
    [isAuthenticated, input, loading]
  );

  const voice = useVoiceAgent({
    getSocketUrl: getVoiceSocketUrl,
    onConversationMessage: ({ role, content }) => {
      setMessages((prev) => prev.concat([{ role, content }]));
      setIsChatPanelOpen(true);
    },
    onVoiceError: (message) => {
      setMessages((prev) => prev.concat([{ role: 'assistant', content: message }]));
      setIsChatPanelOpen(true);
    },
    onToolEvent: (event) => {
      if (event.phase === 'start') {
        const actionText = String(event.action || event.name || 'Processing...');
        setAgentActions((prev) => [...prev, { text: actionText, done: false }]);
        // Open the chat panel so the user can see the train of thought
        setIsChatPanelOpen(true);
      } else if (event.phase === 'result') {
        setAgentActions((prev) => {
          if (prev.length === 0) return prev;
          const updated = [...prev];
          updated[updated.length - 1] = { ...updated[updated.length - 1], done: true };
          return updated;
        });
      } else if (event.phase === 'clear') {
        setAgentActions([]);
      }
    }
  });
  const voiceListening = voice.listening;
  const stopVoiceCapture = voice.stopVoiceCapture;

  useEffect(() => {
    if (mode === 'text') {
      setIsChatPanelOpen(true);
      if (voiceListening) {
        stopVoiceCapture();
      }
    }
  }, [mode, voiceListening, stopVoiceCapture]);

  const runPrompt = useCallback(async function runPrompt(message) {
    const clean = message.trim();
    if (!clean || !companyUid.trim() || !authToken.trim()) return;

    const assistantId = crypto.randomUUID();
    let streamedText = '';

    setMessages((prev) =>
      prev.concat([{ role: 'user', content: clean }, { id: assistantId, role: 'assistant', content: '' }])
    );
    setIsChatPanelOpen(true);
    setLoading(true);
    setAgentActions([]);

    try {
      await streamMessage(
        {
          companyUid: companyUid.trim(),
          message: clean,
          sessionId,
          mode: 'text',
          enableWebBrowsing: true,
          authToken
        },
        (event) => {
          const type = String(event?.type || '');

          // Show tool actions as loading indicators
          if (type === 'tool_call') {
            const actionText = String(event?.action || event?.name || 'Processing...');
            setAgentActions((prev) => [...prev, { text: actionText, done: false }]);
            return;
          }

          if (type === 'tool_result') {
            setAgentActions((prev) => {
              if (prev.length === 0) return prev;
              const updated = [...prev];
              updated[updated.length - 1] = { ...updated[updated.length - 1], done: true };
              return updated;
            });
            return;
          }

          if (type === 'status' || type === 'warning' || type === 'error') return;

          if (type === 'text_delta') {
            // Clear agent actions once text starts flowing
            setAgentActions([]);
            streamedText += String(event?.delta || '');
            setMessages((prev) =>
              prev.map((msg) => (msg.id === assistantId ? { ...msg, content: streamedText } : msg))
            );
            return;
          }

          if (type === 'text_final') {
            setAgentActions([]);
            const finalReply = String(event?.reply || streamedText || '').trim();
            setMessages((prev) =>
              prev.map((msg) => (msg.id === assistantId ? { ...msg, content: finalReply } : msg))
            );
            return;
          }

          if (type === 'done') {
            setAgentActions([]);
            const strongest = String(event?.strongest_candidate || '').trim();
            if (strongest) {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === assistantId
                    ? { ...msg, content: `${msg.content}\n\nCurrent strongest candidate: ${strongest}`.trim() }
                    : msg
                )
              );
            }
          }
        }
      );
    } catch (error) {
      setAgentActions([]);
      setMessages((prev) =>
        prev.map((msg) => (msg.id === assistantId ? { ...msg, content: 'Error: ' + error.message } : msg))
      );
    } finally {
      setLoading(false);
      setAgentActions([]);
    }
  }, [companyUid, authToken, sessionId]);

  const submitText = useCallback(function submitText(event) {
    event.preventDefault();
    if (!canSendText) return;
    const message = input;
    setInput('');
    runPrompt(message);
  }, [canSendText, input, runPrompt]);

  // Check first visit after auth resolves
  useEffect(() => {
    if (!user || !authReady || welcomeChecked.current) return;
    welcomeChecked.current = true;
    checkFirstVisit().then((isFirst) => {
      setWelcomePhase(isFirst ? 'welcome' : 'done');
    });
  }, [user, authReady]);

  if (!mediaLoaded || !authReady || (!user && authLoading)) {
    return React.createElement(
      'main',
      { className: 'login-screen' },
      React.createElement('p', { className: 'login-subtitle' }, 'Loading authentication...')
    );
  }

  if (!user) {
    return React.createElement(LoginScreen, {
      onLogin: loginWithEmail,
      loading: authLoading,
      error: authError
    });
  }

  // First-time visitor: show welcome sequence
  if (welcomePhase === 'checking') {
    return React.createElement(
      'main',
      { className: 'login-screen' },
      React.createElement('p', { className: 'login-subtitle' }, '')
    );
  }

  if (welcomePhase === 'welcome') {
    return React.createElement(SigmundWelcome, {
      onContinue: () => setWelcomePhase('done')
    });
  }

  return React.createElement(
    'main',
    { className: 'sigmund-shell' + (isChatPanelOpen ? ' chat-open' : ' chat-closed') },
    React.createElement(
      'div',
      { className: 'top-actions' },
      React.createElement(
        'button',
        {
          type: 'button',
          className: 'chat-toggle-btn',
          onClick: () => setIsChatPanelOpen((open) => !open)
        },
        isChatPanelOpen ? 'Close Chat' : 'Open Chat'
      ),
      React.createElement('button', { type: 'button', className: 'logout-btn', onClick: logout }, 'Log Out')
    ),
    React.createElement(
      'div',
      { className: 'sigmund-workspace' + (isChatPanelOpen ? ' chat-open' : ' chat-closed') },
      React.createElement(
        'section',
        { className: 'sigmund-main' + (!mode ? ' welcome' : '') },
        React.createElement(
          'div',
          { className: 'sigmund-hero' },
          React.createElement('h1', { className: 'title' }, 'SIGMUND-v0-experimental'),
          React.createElement('p', { className: 'subtitle' }, 'Dear world: Hello.'),
          React.createElement(ModeSwitch, {
            mode,
            onVoice: () => setMode('voice'),
            onText: () => setMode('text')
          })
        ),
        mode === 'voice'
          ? React.createElement(VoiceConsole, {
            listening: voiceListening,
            loading,
            speaker: voice.speaker,
            micLevel: voice.micLevel,
            canUseVoice,
            onStartVoice: () =>
              voice.startVoiceCapture({
                companyUid: companyUid.trim(),
                authToken: authToken.trim()
              }),
            onStopVoice: stopVoiceCapture,
            onSwitchToText: () => setMode('text')
          })
          : null
      ),
      React.createElement(ChatSidePanel, {
        isOpen: isChatPanelOpen,
        onClose: () => setIsChatPanelOpen(false),
        messages,
        agentActions,
        mode,
        inputValue: input,
        onInputChange: setInput,
        onSubmit: submitText,
        canSend: canSendText
      })
    )
  );
}
